import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type FollowRelation } from "@/lib/forex-api";

type ProfitItem = {
  object_id?: string;
  object_type?: string;
  name?: string;
  symbols?: string[];
  net_profit?: number;
  success_rate?: number;
  max_drawdown?: number;
  status?: string;
};

function asNumber(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function formatTime(timestamp?: number) {
  if (!timestamp) return "--";
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

export default function ProfitDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const objectId = searchParams.get("id") || "";
  const [detail, setDetail] = useState<ProfitItem | null>(null);
  const [relation, setRelation] = useState<FollowRelation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([forexApi.getProfitDashboard(), forexApi.listFollowRelations()])
      .then(([dashboard, relations]) => {
        if (cancelled) return;
        const item = dashboard.items.find((entry) => String(entry.object_id) === objectId) as ProfitItem | undefined;
        const matchedRelation = relations.items.find((entry) => entry.relation_id === objectId) || null;
        setDetail(item || null);
        setRelation(matchedRelation);
        if (!item) setError("未找到收益统计对象，请从盈利表现大盘重新进入。");
      })
      .catch(() => {
        if (!cancelled) setError("收益详情接口不可用，请稍后重试。");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [objectId]);

  const netProfit = asNumber(detail?.net_profit, relation?.total_pnl ?? 0);
  const winRate = asNumber(detail?.success_rate, relation?.success_rate ?? 0);
  const maxDrawdown = asNumber(detail?.max_drawdown, relation?.max_drawdown ?? 0);
  const symbols = detail?.symbols || relation?.symbols || [];
  const name = detail?.name || relation?.agent_name || objectId || "收益统计对象";

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "盈利表现大盘", href: "/forex/profit-dashboard" }, { label: "收益分析详情" }]} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">{name}</h2>
          <p className="text-xs text-text-muted mt-0.5">{detail?.object_type || "follow_relation"} · {objectId || "未选择对象"}</p>
        </div>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line text-sm" />返回收益大盘
        </button>
      </div>

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}
      {!loading && error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {!loading && detail && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">净收益</p><p className={`text-lg font-semibold font-mono ${netProfit >= 0 ? "text-emerald-500" : "text-red-500"}`}>{netProfit >= 0 ? "+" : "-"}{Math.abs(netProfit).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">胜率</p><p className="text-lg font-semibold font-mono text-text-primary">{winRate}%</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">最大回撤</p><p className={`text-lg font-semibold font-mono ${maxDrawdown >= 30 ? "text-red-500" : maxDrawdown >= 15 ? "text-amber-500" : "text-emerald-500"}`}>{maxDrawdown}%</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">状态</p><p className="text-xs font-semibold mt-1 text-text-primary">{detail.status || relation?.status || "--"}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">持仓</p><p className="text-lg font-semibold font-mono text-text-primary">{relation?.current_positions ?? 0}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">更新时间</p><p className="text-xs font-mono text-text-primary mt-1">{formatTime(relation?.updated_at)}</p></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">统计对象</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: "对象 ID", value: objectId, mono: true },
                  { label: "对象类型", value: detail.object_type || "--" },
                  { label: "智能体", value: relation?.agent_name || name },
                  { label: "跟单账号", value: relation?.follower_account_masked || "--", mono: true },
                  { label: "经纪商", value: relation?.broker || "--" },
                  { label: "服务器", value: relation?.server || "--", mono: true },
                  { label: "跟单模式", value: relation?.copy_mode || "--" },
                  { label: "跟单值", value: relation ? String(relation.copy_value) : "--", mono: true },
                  { label: "最后指令", value: relation?.last_command_id || "--", mono: true },
                ].map((row) => (
                  <div key={row.label} className="bg-surface/50 rounded-md p-2.5">
                    <span className="text-[10px] text-text-muted font-medium uppercase">{row.label}</span>
                    <p className={`text-xs text-text-primary mt-0.5 ${row.mono ? "font-mono" : ""}`}>{row.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">外汇品种</h3>
              <div className="flex flex-wrap gap-1.5">
                {symbols.map((symbol) => <span key={symbol} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-500">{symbol}</span>)}
                {symbols.length === 0 && <p className="text-xs text-text-muted">暂无品种</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-surface-border p-4">
            <h3 className="text-sm font-semibold text-text-primary mb-3">相关入口</h3>
            <div className="flex items-center gap-3 flex-wrap">
              {relation && <Link to={`/forex/follow/detail?id=${relation.relation_id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap"><i className="ri-user-follow-line text-sm" />跟单详情</Link>}
              {relation && <Link to="/forex/account-commands" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap"><i className="ri-file-copy-line text-sm" />账号命令</Link>}
              {relation?.agent_id && <Link to={`/forex/agents/detail?id=${relation.agent_id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap"><i className="ri-robot-2-line text-sm" />智能体</Link>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
